import React, { useRef, useState, useEffect } from "react";
import { Instagram, Play, Heart, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { gallery, club } from "../mock";
import { useReveal } from "../hooks/useReveal";
import { useSettings } from "../context/SettingsContext";

// Fallback tiles built from club photos when no managed/live feed exists.
const fallbackFeed = gallery.map((g, i) => ({ ...g, isReel: i % 2 === 1 }));
const IG_URL = "https://instagram.com/atletico_soflo";

const resolveSize = (p) => {
  const s = p?.sizes || {};
  const pick = s.medium || s.large || s.small || s.full;
  if (typeof pick === "string") return pick;
  if (pick && typeof pick === "object") return pick.mediaUrl || pick.src || pick.url;
  return p?.mediaUrl || p?.thumbnailUrl || "";
};

export default function InstagramFeed() {
  const { ref, inView } = useReveal();
  const scroller = useRef(null);
  const settings = useSettings();
  const beholdId = (settings?.beholdFeedId || "").trim();
  const [beholdPosts, setBeholdPosts] = useState(null);

  // Live auto-updating feed via Behold (client-side fetch, CORS-enabled).
  useEffect(() => {
    if (!beholdId) { setBeholdPosts(null); return; }
    const url = beholdId.startsWith("http") ? beholdId : `https://feeds.behold.so/${beholdId}`;
    let active = true;
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (!active) return;
        const posts = (d.posts || [])
          .map((p) => ({ src: resolveSize(p), caption: p.prunedCaption || p.caption || "", link: p.permalink || IG_URL, isReel: p.mediaType === "VIDEO" }))
          .filter((p) => p.src);
        setBeholdPosts(posts);
      })
      .catch(() => active && setBeholdPosts(null));
    return () => { active = false; };
  }, [beholdId]);

  const managed = (settings?.followFeed || []).filter((f) => f.image);
  const feed = (beholdPosts && beholdPosts.length)
    ? beholdPosts
    : managed.length
      ? managed.map((f) => ({ src: f.image, caption: f.caption || "", link: f.link || IG_URL, isReel: !!f.isReel }))
      : fallbackFeed.map((f) => ({ ...f, link: IG_URL }));

  const scrollBy = (dir) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  return (
    <section id="follow" className="relative py-24 bg-background">
      <div className="max-w-7xl mx-auto px-5">
        <div ref={ref} className={`reveal ${inView ? "in-view" : ""} flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6`}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[2px] w-10 bg-gold" />
              <span className="section-label text-gold text-xs">Follow Us</span>
            </div>
            <h2 className="display-font uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              <span className="text-gold">{club.instagram}</span>
            </h2>
            <p className="text-white/60 font-light mt-4 max-w-md">
              Latest photos and reels from matchdays, training and life inside the club.
            </p>
          </div>
          <div className="flex items-center gap-3 self-start">
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => scrollBy(-1)}
                aria-label="Scroll left"
                data-testid="feed-scroll-left"
                className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => scrollBy(1)}
                aria-label="Scroll right"
                data-testid="feed-scroll-right"
                className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
            <a
              href={IG_URL}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 bg-gold text-black cond-font uppercase tracking-widest text-sm font-semibold px-6 py-3.5 rounded-sm hover:bg-gold-light transition-colors"
            >
              <Instagram size={18} /> Follow on Instagram
              <ExternalLink size={15} className="group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
        </div>

        <div
          ref={scroller}
          data-testid="feed-scroller"
          className="mt-12 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 px-5 [scrollbar-width:thin] [scrollbar-color:rgba(229,184,51,0.5)_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-gold/50 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-white/5"
        >
          {feed.map((item, i) => (
            <a
              key={item.src || i}
              href={/^https?:\/\//i.test(item.link || "") ? item.link : IG_URL}
              target="_blank"
              rel="noreferrer"
              className="group relative shrink-0 w-56 sm:w-64 aspect-square overflow-hidden rounded-lg border border-white/10 snap-start"
            >
              <img src={item.src} alt={item.caption} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              {item.isReel && (
                <span className="absolute top-2 right-2 text-white drop-shadow">
                  <Play size={16} fill="white" />
                </span>
              )}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 text-white">
                <Heart size={18} fill="white" />
                <Instagram size={20} />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
